import 'client-only';

import { useMemo } from 'react';

import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { getRxPurchaseContextPricesFromEstimates } from '@/components/flow/steps/resubscription-checkout/utils/helpers';
import {
  getRxPurchaseContextPricesFromAnyItemPrices,
  getRxPurchaseContextPrice,
  getRxItemId,
} from '@/components/purchaseContextToggle/helpers/purchaseContextToggle';

import { usePurchaseContext } from './usePurchaseContext';

export const useRxPurchaseContextPrice = () => {
  const { purchaseContext } = usePurchaseContext();
  const data = useDataProvider();

  if (!data) {
    throw new Error(
      'useFullSizeItemPrices can only be used within an instance of DataProvider.',
    );
  }

  // This is more complex than it should have to be because the data used to calculate these prices
  // may come from different sources depending on the flow. The following is capable of being used
  // when product selection, checkout, or resubscription checkout data is available.
  const rxPurchaseContextPrices = useMemo(() => {
    if (data.productSelection || data.checkout) {
      const { rxCard, oneTimeItemPrices, trialSizeItemPrices } =
        data.productSelection! ?? data.checkout!;

      const rxItemId = getRxItemId(rxCard);

      return getRxPurchaseContextPricesFromAnyItemPrices(rxItemId, {
        onetime: oneTimeItemPrices,
        trial: trialSizeItemPrices,
      });
    }

    if (data.resubscriptionCheckoutCombinedData) {
      const { estimate, onetimeEstimate } =
        data.resubscriptionCheckoutCombinedData;

      return getRxPurchaseContextPricesFromEstimates([
        estimate,
        onetimeEstimate,
      ]);
    }

    throw new Error(
      'Not able to determine rxPurchaseContextPrices based on data available from dataProvider.',
    );
  }, [data]);

  const rxPurchaseContextPrice = useMemo(
    () => getRxPurchaseContextPrice(rxPurchaseContextPrices, purchaseContext),
    [rxPurchaseContextPrices, purchaseContext],
  );

  return {
    rxPurchaseContextPrices,
    rxPurchaseContextPrice,
  };
};
